import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    FormControl,
    FormLabel,
    Flex,
    Box,
    Image,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { CloseIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

interface OrganizationFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: FormData) => void;
    organization?: any;
    isLoading?: boolean;
}

const OrganizationFormModal: React.FC<OrganizationFormModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    organization,
    isLoading = false,
}) => {
    const { t } = useTranslation("common");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [domain, setDomain] = useState("");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [faviconFile, setFaviconFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const faviconInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (organization) {
            setName(organization.name || "");
            setEmail(organization.email || "");
            setDomain(organization.domain || "");
            setLogoPreview(organization.logo || null);
            setFaviconPreview(organization.favicon || null);
        } else {
            resetForm();
        }
    }, [organization, isOpen]);

    const resetForm = () => {
        setName("");
        setEmail("");
        setDomain("");
        setLogoFile(null);
        setFaviconFile(null);
        setLogoPreview(null);
        setFaviconPreview(null);
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                alert(t("superAdmin.pleaseSelectImage"));
                return;
            }
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                alert(t("superAdmin.pleaseSelectImage"));
                return;
            }
            setFaviconFile(file);
            setFaviconPreview(URL.createObjectURL(file));
        }
    };

    const removeLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
        if (logoInputRef.current) {
            logoInputRef.current.value = "";
        }
    };

    const removeFavicon = () => {
        setFaviconFile(null);
        setFaviconPreview(null);
        if (faviconInputRef.current) {
            faviconInputRef.current.value = "";
        }
    };

    const handleSubmit = () => {
        if (!name || !email || !domain) {
            return;
        }

        const formData = new FormData();
        formData.append("name", name);
        formData.append("email", email);
        formData.append("domain", domain);

        // Append logo file if a new one is selected
        if (logoFile) {
            formData.append("logo", logoFile);
        }

        // Append favicon file if a new one is selected
        if (faviconFile) {
            formData.append("favicon", faviconFile);
        }

        onSubmit(formData);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const isFormValid = name && email && domain;

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent borderRadius="12px" bg="white">
                <ModalHeader>
                    {organization ? t("superAdmin.editOrganization") : t("superAdmin.createOrganization")}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Flex direction="column" gap={4}>
                        <FormControl isRequired>
                            <FormLabel>{t("superAdmin.organizationName")}</FormLabel>
                            <Input
                                placeholder={t("superAdmin.enterOrganizationName")}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>{t("admin.email")}</FormLabel>
                            <Input
                                type="email"
                                placeholder={t("superAdmin.emailPlaceholder")}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>{t("superAdmin.domain")}</FormLabel>
                            <Input
                                placeholder={t("superAdmin.domainPlaceholder")}
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>{t("superAdmin.logo")}</FormLabel>
                            <Flex direction="column" gap={2}>
                                {logoPreview && (
                                    <Box position="relative" width="120px" height="120px">
                                        <Image
                                            src={logoPreview}
                                            alt="Logo preview"
                                            width="100%"
                                            height="100%"
                                            objectFit="contain"
                                            borderRadius="8px"
                                            border="1px solid"
                                            borderColor="gray.200"
                                        />
                                        <Box
                                            position="absolute"
                                            top="-8px"
                                            right="-8px"
                                            bg="red.500"
                                            borderRadius="50%"
                                            width="24px"
                                            height="24px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            cursor="pointer"
                                            onClick={removeLogo}
                                        >
                                            <CloseIcon width="12px" height="12px" color="white" />
                                        </Box>
                                    </Box>
                                )}
                                <Input
                                    ref={logoInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                    display="none"
                                    id="logo-upload"
                                />
                                <Button
                                    as="label"
                                    htmlFor="logo-upload"
                                    variant="outline"
                                    cursor="pointer"
                                    size="sm"
                                >
                                    {logoPreview ? t("superAdmin.changeLogo") : t("superAdmin.uploadLogo")}
                                </Button>
                            </Flex>
                        </FormControl>

                        <FormControl>
                            <FormLabel>{t("superAdmin.favicon")}</FormLabel>
                            <Flex direction="column" gap={2}>
                                {faviconPreview && (
                                    <Box position="relative" width="64px" height="64px">
                                        <Image
                                            src={faviconPreview}
                                            alt="Favicon preview"
                                            width="100%"
                                            height="100%"
                                            objectFit="contain"
                                            borderRadius="8px"
                                            border="1px solid"
                                            borderColor="gray.200"
                                        />
                                        <Box
                                            position="absolute"
                                            top="-8px"
                                            right="-8px"
                                            bg="red.500"
                                            borderRadius="50%"
                                            width="24px"
                                            height="24px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            cursor="pointer"
                                            onClick={removeFavicon}
                                        >
                                            <CloseIcon width="12px" height="12px" color="white" />
                                        </Box>
                                    </Box>
                                )}
                                <Input
                                    ref={faviconInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFaviconChange}
                                    display="none"
                                    id="favicon-upload"
                                />
                                <Button
                                    as="label"
                                    htmlFor="favicon-upload"
                                    variant="outline"
                                    cursor="pointer"
                                    size="sm"
                                >
                                    {faviconPreview ? t("superAdmin.changeFavicon") : t("superAdmin.uploadFavicon")}
                                </Button>
                            </Flex>
                        </FormControl>
                    </Flex>
                </ModalBody>

                <ModalFooter>
                    <Button variant="outline" mr={3} onClick={handleClose}>
                        {t("admin.cancel")}
                    </Button>
                    <Button
                        bg="#0E0E0E"
                        color="white"
                        onClick={handleSubmit}
                        isDisabled={!isFormValid || isLoading}
                        isLoading={isLoading}
                    >
                        {organization ? t("superAdmin.update") : t("superAdmin.create")}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default OrganizationFormModal;
